import { Component, input, output, signal, computed, effect } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { DividerComponent } from '../divider/divider.component';
import {
  faChevronLeft,
  faChevronRight,
} from '@fortawesome/pro-regular-svg-icons';
import { SidebarItem } from './sidebar.types';
import { SidebarNavItemComponent } from './sidebar-nav-item.component';

export type { SidebarItem } from './sidebar.types';

// =============================================================================
// Sidebar Component
// Collapsible navigation sidebar with recursive item rendering
// =============================================================================
@Component({
  selector: 'ams-sidebar',
  standalone: true,
  imports: [CommonModule, RouterLink, FontAwesomeModule, DividerComponent, SidebarNavItemComponent],
  template: `
    <aside class="ams-sidebar" [class.ams-sidebar--collapsed]="collapsed()">
      <!-- Logo Section -->
      @if (collapsed()) {
        <!-- Collapsed: entire header is the expand button -->
        <button
          type="button"
          class="ams-sidebar__header ams-sidebar__header--btn"
          (click)="toggleCollapse()"
          aria-label="Expand sidebar"
        >
          <div class="ams-sidebar__logo">
            @if(logoUrl()!='' && logoUrl()!=undefined) {
              <img [src]="logoUrl()" alt="{{ logoAltText() }}" class="ams-sidebar__logo-image ams-sidebar__logo-image--collapsed" (error)="logoError.set(true)" [hidden]="logoError()" />
              @if (logoError()) {
                <span class="ams-sidebar__logo-text ams-sidebar__logo-text--collapsed">{{ logoAltText().charAt(0) }}</span>
              }
            } @else {
              <span class="ams-sidebar__logo-text ams-sidebar__logo-text--collapsed">{{ logoAltText().charAt(0) }}</span>
            }
          </div>
        </button>
      } @else {
        <!-- Expanded: normal header with logo + chevron toggle -->
        <div class="ams-sidebar__header">
          <!-- Logo/name rendered exactly as before; an optional contextual badge
               (chapter / member org) is stacked underneath it. -->
          <div class="ams-sidebar__brand" [class.ams-sidebar__brand--with-badge]="!!brandBadge()">
            <div class="ams-sidebar__logo">
              @if(logoUrl()!='' && logoUrl()!=undefined) {
                <img [src]="logoUrl()" alt="{{ logoAltText() }}" class="ams-sidebar__logo-image" (error)="logoError.set(true)" [hidden]="logoError()" />
                @if (logoError()) {
                  <span class="ams-sidebar__logo-text" [title]="logoAltText()">{{ logoAltText() }}</span>
                }
              } @else {
                <span class="ams-sidebar__logo-text" [title]="logoAltText()">{{ logoAltText() }}</span>
              }
            </div>
            @if (brandBadge()) {
              <span class="ams-sidebar__brand-badge" [title]="brandBadge()">{{ brandBadge() }}</span>
            }
          </div>
          <button
            type="button"
            class="ams-sidebar__toggle"
            (click)="toggleCollapse()"
            aria-label="Collapse sidebar"
          >
            <fa-icon [icon]="faChevronLeft" size="sm"></fa-icon>
          </button>
        </div>
      }

      <!-- Navigation -->
      <nav class="ams-sidebar__nav">
        @for (item of items(); track item.id) {
          @if (item.hidden) {
            <!-- Hidden item -->
          } @else if (item.divider) {
            <ams-divider></ams-divider>
          } @else if (item.section) {
            @if (!collapsed()) {
              <div class="ams-sidebar__section">{{ item.section }}</div>
            }
          } @else {
            <ams-sidebar-nav-item
              [item]="item"
              [sidebarCollapsed]="collapsed()"
              [depth]="0"
            />
          }
        }
      </nav>

      <!-- Footer Slot -->
      <div class="ams-sidebar__footer">
        <ng-content select="[slot=footer]" />
      </div>
    </aside>
  `,
  styleUrl: './sidebar.component.scss',
})
export class SidebarComponent {
  // Font Awesome icons
  readonly faChevronLeft = faChevronLeft;
  readonly faChevronRight = faChevronRight;

  // Inputs
  readonly items = input<SidebarItem[]>([]);
  readonly logo = input<string>('/assets/logo.svg');
  readonly logoIcon = input<string>('/assets/logo-icon.svg');
  readonly logoAlt = input<string>('Logo');
  // Optional contextual badge shown in the expanded header, stacked under the
  // logo/name (e.g. the active chapter or the member's organization).
  readonly brandBadge = input<string>('');
  readonly initialCollapsed = input<boolean>(false);

  // Internal state
  readonly collapsed = signal<boolean>(false);
  readonly logoError = signal(false);

  // Outputs
  readonly collapsedChange = output<boolean>();

  constructor() {
    // Tracks initialCollapsed() for its whole lifetime, not just at
    // construction — a consumer that only sets it once (the common case)
    // sees no change from before; one that updates it later (e.g. to force
    // a collapse on a mobile-width viewport) now actually takes effect,
    // which a plain one-time read in the constructor never picked up.
    effect(() => {
      this.collapsed.set(this.initialCollapsed());
    });
  }

  logoUrl = computed(() => this.logo());
  logoAltText = computed(() => this.logoAlt());

  toggleCollapse(): void {
    const newState = !this.collapsed();
    this.collapsed.set(newState);
    this.collapsedChange.emit(newState);
  }
}
